<template>
  <div class="honors-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchPageData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 内容区域 -->
        <div v-else class="content-wrapper">
          <!-- 富文本内容（如果有） -->
          <div
            v-if="richtextHtml && richtextHtml.trim()"
            class="richtext-content"
            v-html="richtextHtml"
          ></div>

          <!-- 荣誉图片轮播 -->
          <div v-if="imageList && imageList.length > 0" class="honors-carousel-section">
            <h2 class="carousel-title">{{ t('honors.certificates') }}</h2>

            <!-- 轮播图容器 -->
            <div class="carousel-container">
              <div class="carousel-wrapper">
                <!-- 轮播图主区域 -->
                <div class="carousel-main">
                  <div
                    class="carousel-track"
                    :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
                  >
                    <div
                      v-for="(image, index) in imageList"
                      :key="index"
                      class="carousel-slide"
                    >
                      <div class="slide-content">
                        <div class="image-container">
                          <img
                            :src="image.path"
                            :alt="image.name"
                            class="honor-image"
                            @error="handleImageError"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 导航箭头 -->
                  <button
                    v-if="imageList.length > 1"
                    class="carousel-arrow prev-arrow"
                    @click="prevSlide"
                    :disabled="currentIndex === 0"
                  >
                    <el-icon size="24">
                      <ArrowLeft />
                    </el-icon>
                  </button>

                  <button
                    v-if="imageList.length > 1"
                    class="carousel-arrow next-arrow"
                    @click="nextSlide"
                    :disabled="currentIndex === imageList.length - 1"
                  >
                    <el-icon size="24">
                      <ArrowRight />
                    </el-icon>
                  </button>

                  <!-- 图片名称展示 -->
                  <div v-if="imageList[currentIndex]?.name" class="image-name">
                    {{ imageList[currentIndex].name }}
                  </div>
                </div>

                <!-- 缩略图导航 -->
                <div v-if="imageList.length > 1" class="thumbnail-nav">
                  <div
                    v-for="(image, index) in imageList"
                    :key="index"
                    class="thumbnail-item"
                    :class="{ active: currentIndex === index }"
                    @click="goToSlide(index)"
                  >
                    <div class="thumbnail-container">
                      <img
                        :src="image.path"
                        :alt="image.name"
                        class="thumbnail-image"
                      />
                      <div class="thumbnail-overlay"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 空状态（当没有任何内容时显示） -->
          <div v-else class="empty-state">
            <div class="empty-icon">
              <el-icon size="80" color="var(--el-color-primary-light-8)">
                <Trophy />
              </el-icon>
            </div>
            <h3 class="empty-title">{{ t('common.no_data') }}</h3>
            <p class="empty-description">{{ t('honors.no_honors') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { Warning, ArrowLeft, ArrowRight, Trophy } from '@element-plus/icons-vue'
import { getSinglePageContent } from "@/assets/api/website";

// 使用 i18n
const { locale, t } = useI18n()

// 响应式数据
const pageData = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')
const currentIndex = ref(0)
const autoPlayTimer = ref(null)
const isAutoPlaying = ref(true)

// 计算属性
const pageTitle = computed(() => {
  return pageData.value?.page_name || t('menu.honors')
})

const richtextHtml = computed(() => {
  return pageData.value?.content?.richtext || ''
})

const imageList = computed(() => {
  return pageData.value?.content?.image_list || []
})

// 轮播图方法
const nextSlide = () => {
  if (currentIndex.value < imageList.value.length - 1) {
    currentIndex.value++
  } else {
    currentIndex.value = 0 // 循环到第一张
  }
  resetAutoPlay()
}

const prevSlide = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  } else {
    currentIndex.value = imageList.value.length - 1 // 循环到最后一张
  }
  resetAutoPlay()
}

const goToSlide = (index) => {
  currentIndex.value = index
  resetAutoPlay()
}

// 自动播放
const startAutoPlay = () => {
  if (imageList.value.length <= 1) return

  clearInterval(autoPlayTimer.value)
  autoPlayTimer.value = setInterval(() => {
    if (isAutoPlaying.value) {
      nextSlide()
    }
  }, 5000) // 5秒自动切换
}

const stopAutoPlay = () => {
  isAutoPlaying.value = false
  clearInterval(autoPlayTimer.value)
}

const resetAutoPlay = () => {
  stopAutoPlay()
  setTimeout(() => {
    isAutoPlaying.value = true
    startAutoPlay()
  }, 100)
}

// 图片加载失败处理
const handleImageError = (event) => {
  event.target.style.display = 'none'
  const placeholder = document.createElement('div')
  placeholder.className = 'image-error-placeholder'
  placeholder.innerHTML = `
    <div class="placeholder-icon">
      <el-icon size="40" color="var(--el-color-primary-light-6)">
        <Picture />
      </el-icon>
    </div>
    <p class="placeholder-text">图片加载失败</p>
  `
  event.target.parentElement.appendChild(placeholder)
}

// 获取页面数据
const fetchPageData = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    // 使用当前的语言
    const currentLocale = locale.value

    const response = await getSinglePageContent({
      lang: currentLocale,
      path: '/honors'
    })

    if (response && response.content) {
      pageData.value = response

      // 如果有图片，启动自动播放
      if (imageList.value.length > 0) {
        nextTick(() => {
          startAutoPlay()
        })
      }
    } else {
      pageData.value = null
    }
  } catch (error) {
    console.error('获取页面数据失败:', error)
    errorMessage.value = t('common.load_failed')
  } finally {
    isLoading.value = false
  }
}

// 监听语言变化，重新加载数据
watch(locale, () => {
  fetchPageData()
})

// 组件挂载时获取数据
onMounted(() => {
  fetchPageData()
})

// 组件卸载时清理定时器
onUnmounted(() => {
  stopAutoPlay()
  clearInterval(autoPlayTimer.value)
})
</script>

<style scoped lang="scss">
// 导入全局样式变量
@import '@/assets/css/common';

.honors-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f9fbf7 0%, #ffffff 100%);
}

// 页面头部样式
.page-header {
  padding: 60px 0 40px;
  background: linear-gradient(135deg, rgba($primary-color, 0.03) 0%, rgba($primary-color, 0.01) 100%);
  border-bottom: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    padding: 40px 0 30px;
  }

  @media (max-width: 480px) {
    padding: 30px 0 20px;
  }
}

.page-title {
  font-family: $font-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: $primary-color-2;
  text-align: center;
  margin-bottom: 20px;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin-bottom: 16px;
  }
}

.header-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, $primary-color, $secondary-color);
  margin: 0 auto;
  border-radius: 2px;

  @media (max-width: 480px) {
    width: 60px;
    height: 3px;
  }
}

// 主要内容区域
.page-content {
  padding: 60px 0;

  @media (max-width: 768px) {
    padding: 40px 0;
  }

  @media (max-width: 480px) {
    padding: 30px 0;
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 60px;
}

// 富文本内容样式
.richtext-content {
  background: $white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);
  line-height: 1.8;

  // 基础段落样式
  p {
    margin-bottom: 1.5em;
    color: $gray-80;
    font-size: 1.125rem;
    text-align: justify;

    &:last-child {
      margin-bottom: 0;
    }
  }

  // 响应式调整
  @media (max-width: 768px) {
    padding: 30px 24px;

    p {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    padding: 24px 20px;

    p {
      font-size: 0.9375rem;
    }
  }
}

// 荣誉轮播区域
.honors-carousel-section {
  background: $white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    padding: 30px 24px;
  }

  @media (max-width: 480px) {
    padding: 24px 20px;
  }
}

.carousel-title {
  font-family: $font-serif;
  font-size: 1.75rem;
  font-weight: 600;
  color: $primary-color-2;
  text-align: center;
  margin-bottom: 40px;
  position: relative;
  padding-bottom: 16px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, $primary-color, $secondary-color);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 30px;
    padding-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 1.375rem;
    margin-bottom: 24px;
  }
}

.carousel-container {
  width: 100%;
}

.carousel-wrapper {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

// 轮播图主区域
.carousel-main {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 12px;
  background: $gray-10;
  min-height: 400px;
}

.carousel-track {
  display: flex;
  transition: transform 0.5s ease;
  height: 100%;
}

.carousel-slide {
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.slide-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-container {
  width: 100%;
  max-width: 600px;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    height: 350px;
    max-width: 500px;
  }

  @media (max-width: 480px) {
    height: 250px;
    max-width: 100%;
    padding: 15px;
  }
}

.honor-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
}

// 图片加载失败占位符
.image-error-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: $gray-10;
  border-radius: 8px;

  .placeholder-icon {
    margin-bottom: 12px;
    opacity: 0.6;
  }

  .placeholder-text {
    font-size: 0.875rem;
    color: $gray-60;
  }
}

// 导航箭头
.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  background: rgba($white, 0.9);
  border: 1px solid rgba($primary-color, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    background: $white;
    border-color: $primary-color;
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  :deep(.el-icon) {
    color: $primary-color;
    font-weight: bold;
  }

  &.prev-arrow {
    left: 20px;
  }

  &.next-arrow {
    right: 20px;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;

    &.prev-arrow {
      left: 10px;
    }

    &.next-arrow {
      right: 10px;
    }
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;

    :deep(.el-icon) {
      font-size: 18px;
    }
  }
}

// 图片名称
.image-name {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 1.125rem;
  font-weight: 600;
  color: $primary-color-2;
  padding: 12px 20px;
  background: rgba($white, 0.9);
  backdrop-filter: blur(5px);
  border-top: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 10px 16px;
    bottom: 15px;
  }

  @media (max-width: 480px) {
    font-size: 0.9375rem;
    padding: 8px 12px;
    bottom: 10px;
  }
}

// 缩略图导航
.thumbnail-nav {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 0 10px;

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
}

.thumbnail-item {
  width: 80px;
  height: 80px;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba($primary-color, 0.3);
  }

  &.active {
    border-color: $primary-color;
    box-shadow: 0 0 0 2px rgba($primary-color, 0.2);

    .thumbnail-overlay {
      opacity: 0;
    }
  }

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
  }
}

.thumbnail-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba($black, 0.2);
  transition: opacity 0.3s ease;
}

// 加载状态
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba($primary-color, 0.1);
  border-top-color: $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 1rem;
  color: $text-light;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// 错误状态
.error-container {
  text-align: center;
  padding: 60px 20px;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
}

.error-icon {
  margin-bottom: 20px;
  color: #f56c6c;
}

.error-title {
  font-size: 1.5rem;
  color: #f56c6c;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.error-message {
  font-size: 1rem;
  color: $text-light;
  margin-bottom: 20px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.retry-button {
  background: $primary-color;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: $primary-light-3;
    transform: translateY(-2px);
  }
}

// 空状态
.empty-state {
  text-align: center;
  padding: 80px 20px;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }

  @media (max-width: 480px) {
    padding: 40px 16px;
  }
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.7;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
}

.empty-title {
  font-size: 1.5rem;
  color: $text-color;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.empty-description {
  font-size: 1rem;
  color: $text-light;
  max-width: 400px;
  margin: 0 auto;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
}
</style>